import { formatIDR } from "@/lib/constants";
import { classifyIncomeEntry, LEGACY_CATEGORIES } from "@/lib/incomeClassification";

/**
 * LedgerSummaryCards — 149-bs. Extracted from IncomeLedgerDialog (bagian header
 * summary cards + 3 KPI baris kedua). Semua nilai murni derived dari props;
 * tidak boleh menyimpan state supaya recompute logic tetap di parent (single
 * source of truth totals).
 *
 * 149-da (PROMPT 16-ACC-LEGACY-DISPLAY-R1): "Received (Net)" dipecah menjadi
 * "Actual Received Terverifikasi" (hanya PROVEN_PAID) vs "Estimasi Net (Formula)"
 * (AMBIGUOUS, dari formula lama — BUKAN uang aktual). Data legacy tidak dianggap
 * pembayaran nyata sampai diverifikasi manual.
 */
export default function LedgerSummaryCards({ project, contractValue, totals, retPct }) {
  const isExempt = !!project?.is_ppn_exempt;
  const cvDpp = contractValue;
  const cvVat = isExempt ? 0 : Math.round(cvDpp * 0.11);
  const cvGross = cvDpp + cvVat;
  const invDpp = totals.gross;
  const invVat = isExempt ? 0 : Math.round(invDpp * 0.11);
  const invGross = invDpp + invVat;
  const sisaGross = cvGross - invGross;
  const sisaDpp = cvDpp - invDpp;
  const over = (v) => (v < 0 ? "border-rose-400 bg-rose-50" : "");
  const card = (label, val, tone, testid) => (
    <div className={`bg-white border ${tone} p-2`} data-testid={testid}>
      <div className="overline text-[9px] leading-tight">{label}</div>
      <div className="tabular-nums text-[13px] font-bold leading-tight">{formatIDR(val)}</div>
    </div>
  );

  // 149-da: klasifikasi setiap entry & pisahkan verified vs legacy.
  let verifiedReceived = 0;
  let legacyEstNet = 0;
  let needsVerificationCount = 0;
  for (const e of project?.income_entries || []) {
    const cat = classifyIncomeEntry(e, project);
    const net = Number(e.net_received_amount || 0);
    const gross = Number(e.gross_invoice_amount || 0);
    if (cat === LEGACY_CATEGORIES.PROVEN_PAID) verifiedReceived += net || gross;
    else if (cat === LEGACY_CATEGORIES.AMBIGUOUS_RECEIVED_DATE) {
      legacyEstNet += net || 0;
      needsVerificationCount += 1;
    }
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="income-summary">
        {card("Nilai Kontrak (incl PPN)", cvGross, "border-[#0B1D3A] text-[#0B1D3A]", "hdr-contract-gross")}
        {card("DPP Kontrak", cvDpp, "border-slate-300 text-slate-700", "hdr-contract-dpp")}
        {card(isExempt ? "PPN Kontrak (Non-PPN)" : "PPN Kontrak", cvVat, "border-sky-200 bg-sky-50/40 text-sky-800", "hdr-contract-vat")}
        {card("Total Invoice (incl PPN)", invGross, "border-slate-300 text-slate-700", "hdr-invoice-gross")}
        {card("Total DPP Ditagihkan", invDpp, "border-slate-300 text-slate-700", "hdr-invoice-dpp")}
        {card("Sisa Kontrak (incl PPN)", sisaGross, `border-slate-300 text-[#0B1D3A] ${over(sisaGross)}`, "hdr-remain-gross")}
        {card("Sisa DPP", sisaDpp, `border-slate-300 text-[#0B1D3A] ${over(sisaDpp)}`, "hdr-remain-dpp")}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2" data-testid="income-summary-extra">
        <div className="bg-sky-50 border border-sky-200 p-2">
          <div className="overline text-sky-700 text-[9px]">Total PPh Dipotong</div>
          <div data-testid="income-total-pph" className="font-display text-base tabular-nums text-sky-800">{formatIDR(totals.pph)}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 p-2">
          <div className="overline text-amber-700 text-[9px]">Retensi Ditahan ({retPct}%)</div>
          <div data-testid="income-total-retention" className="font-display text-base tabular-nums text-amber-800">{formatIDR(totals.retention)}</div>
        </div>
        <div className="bg-emerald-50 border-2 border-emerald-400 p-2" title="Hanya dari entry yang PROVEN_PAID (bukti transfer eksplisit)">
          <div className="overline text-emerald-700 text-[9px]">Actual Received Terverifikasi</div>
          <div data-testid="income-total-verified" className="font-display text-base tabular-nums text-emerald-800">{formatIDR(verifiedReceived)}</div>
        </div>
        <div className="bg-slate-50 border border-slate-300 p-2" title="Data legacy (AMBIGUOUS_RECEIVED_DATE). Nilai dari formula lama — BUKAN uang aktual diterima. Perlu verifikasi manual.">
          <div className="overline text-slate-600 text-[9px]">Estimasi Net (Formula) · {needsVerificationCount} perlu verifikasi</div>
          <div data-testid="income-total-legacy-est" className="font-display text-base tabular-nums text-slate-700 italic">{formatIDR(legacyEstNet)}</div>
        </div>
      </div>
    </>
  );
}
